import { useRef, type ClipboardEvent, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  /** One character per box: a digit, or a space for an empty box. */
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Labels the group for screen readers. */
  label: string;
}

const onlyDigits = (s: string) => s.replace(/\D/g, "");

/**
 * One box per digit. Typing moves forward and Backspace moves back. A pasted
 * code, or one the browser or password manager fills in (autocomplete
 * "one-time-code" on the first box), is spread across all the boxes.
 */
export default function OtpInput({
  value,
  onChange,
  length = 6,
  disabled = false,
  autoFocus = false,
  label,
}: Props) {
  const { t } = useTranslation();
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => (value[i] ?? " ").trim());

  const focus = (index: number) => {
    const box = inputs.current[Math.max(0, Math.min(length - 1, index))];
    box?.focus();
    box?.select();
  };

  const emit = (next: string[]) => onChange(next.map((d) => d || " ").join(""));

  /** Writes `chars` into the boxes from `start` on, then focuses the next empty one. */
  const fill = (start: number, chars: string) => {
    const next = [...digits];
    chars
      .slice(0, length - start)
      .split("")
      .forEach((d, i) => {
        next[start + i] = d;
      });
    emit(next);
    focus(Math.min(start + chars.length, length - 1));
  };

  const handleChange = (index: number, raw: string) => {
    const typed = onlyDigits(raw);
    if (!typed) {
      const next = [...digits];
      next[index] = "";
      emit(next);
      return;
    }
    // A box that already held a digit now holds two: the old one plus the one
    // just typed (the caret wasn't selecting it). Keep only the new digit.
    if (typed.length === 2 && digits[index]) {
      fill(index, typed.replace(digits[index], "") || digits[index]);
      return;
    }
    // Several digits at once: autofill or a paste the browser didn't route
    // through onPaste. A full-length code always starts at the first box.
    if (typed.length >= length) fill(0, typed.slice(-length));
    else fill(index, typed);
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      e.preventDefault();
      const next = [...digits];
      next[index - 1] = "";
      emit(next);
      focus(index - 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focus(index - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focus(index + 1);
    }
  };

  const handlePaste = (index: number, e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = onlyDigits(e.clipboardData.getData("text"));
    if (!pasted) return;
    e.preventDefault();
    fill(pasted.length >= length ? 0 : index, pasted.slice(0, length));
  };

  return (
    <div role="group" aria-label={label} className="flex justify-between gap-2">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          aria-label={t("auth.verify.digitLabel", { position: index + 1, length })}
          value={digit}
          disabled={disabled}
          autoFocus={autoFocus && index === 0}
          onFocus={(e) => e.target.select()}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
          className={`h-12 w-10 sm:h-14 sm:w-12 rounded-lg border text-center text-xl sm:text-2xl font-semibold text-background-50 caret-accent-300 focus:outline-none focus:ring-2 focus:ring-accent-400 disabled:opacity-60 ${
            digit ? "border-accent-400/60 bg-background-50/15" : "border-background-50/15 bg-background-50/10"
          }`}
        />
      ))}
    </div>
  );
}
